import { Pressable } from '../../../ui/Pressable';
import { SkeletonBlock } from '../../../ui/Skeleton';
import type { Session } from '../../../shell/bridge';
import { Reactions, NoteComposer } from './reactions';
import { LaterPanel } from './lifecycle';
import { assigneeLabel, dueLabel, pillAria, pillLabel, recurChip } from './helpers';
import type { ChoreItem } from './types';

/** The task list — open rows, then the Done section (card MnorjNDwB3of
 *  keeps reactions + receipts on both). One row per chore: cadence chip,
 *  assignee pill, due date, tick-off, note, edit, delete, later. */
export function ChoreList({
  items, failed, open, done, me, partnerPhrase, busyId, armedDelete, laterFor,
  setLaterFor, setLaterNote, noteFor, setNoteFor, noteDraft, setNoteDraft,
  reactBusyId, react, saveNote, openNote, tapAssignee, complete, beginEdit,
  removeChore, pushLater,
}: {
  items: ChoreItem[] | null;
  failed: boolean;
  open: ChoreItem[];
  done: ChoreItem[];
  me: Session | null;
  partnerPhrase: string;
  busyId: string | null;
  armedDelete: string | null;
  laterFor: string | null;
  setLaterFor: (v: string | null) => void;
  setLaterNote: (v: string | null) => void;
  noteFor: string | null;
  setNoteFor: (v: string | null) => void;
  noteDraft: string;
  setNoteDraft: (v: string) => void;
  reactBusyId: string | null;
  react: (chore: ChoreItem, glyph: string) => Promise<void>;
  saveNote: (chore: ChoreItem) => Promise<void>;
  openNote: (chore: ChoreItem) => void;
  tapAssignee: (chore: ChoreItem) => Promise<void>;
  complete: (chore: ChoreItem) => Promise<void>;
  beginEdit: (chore: ChoreItem) => void;
  removeChore: (chore: ChoreItem) => Promise<void>;
  pushLater: (chore: ChoreItem, kind: 'snooze' | 'defer', returnAt?: number) => Promise<void>;
}) {
  return (
    <div className="rq-list ch-list">
      {items === null && !failed && <SkeletonBlock lines={3} />}
      {failed && <p className="rq-empty">Could not load the list.</p>}
      {items !== null && items.length === 0 && !failed && (
        <div className="rq-empty-state" id="ch-seed-state">
          <p className="rq-empty-title">Nothing on the list yet.</p>
          <p className="rq-empty-hint">Add one — bins, a clean, a shop — and pick who is on it.</p>
          {/* Entry state: the library above IS the start. Its chips are
              multi-pick, so the first shelf of items is one tap each and
              a single Add. */}
          <p className="rq-empty-hint">Or pick a few from Common ones above.</p>
        </div>
      )}

      {open.map(chore => {
        const busy = busyId === chore.choreId;
        return (
          <div className="rq-row ch-row" key={chore.choreId} data-chore-id={chore.choreId}>
            <div className="rq-head">
              <span className={`rq-state ${chore.cadence === 'recurring' ? 'is-countered' : 'is-pending'}`}>
                {recurChip(chore)}
              </span>
              <span className="rq-when">{dueLabel(chore)}</span>
            </div>

            <p className="pear-request-text rq-text">{chore.title}</p>

            <div className="ch-row-foot">
              <Pressable
                className="pear-btn ch-assignee"
                disabled={busy}
                aria-label={pillAria(chore, me, partnerPhrase)}
                onClick={() => void tapAssignee(chore)}
              >
                {pillLabel(chore, me)}
              </Pressable>
              <Pressable
                className="pear-btn pear-btn-mustard ch-done"
                disabled={busy}
                aria-label={`Mark ${chore.title} done`}
                onClick={() => void complete(chore)}
              >
                {busy ? '…' : 'Tick off'}
              </Pressable>
              {/* A receipt with the tick (card MnorjNDwB3of): opens the one
                  line composer; saving it also completes the chore. */}
              <Pressable
                className="pear-btn ch-note-open"
                disabled={busy}
                aria-label={`Add a done note to ${chore.title}`}
                aria-expanded={noteFor === chore.choreId}
                onClick={() => (noteFor === chore.choreId ? (setNoteFor(null), setNoteDraft('')) : openNote(chore))}
              >
                Note
              </Pressable>
              {/* Edit loads the row into the composer above; Delete arms on
                  the first tap and removes on the second (card VmoORelj1HuO). */}
              <Pressable
                className="pear-btn ch-edit"
                disabled={busy}
                aria-label={`Edit ${chore.title}`}
                onClick={() => beginEdit(chore)}
              >
                Edit
              </Pressable>
              <Pressable
                className={`pear-btn ch-delete${armedDelete === chore.choreId ? ' is-armed' : ''}`}
                disabled={busy}
                aria-label={armedDelete === chore.choreId ? `Confirm delete ${chore.title}` : `Delete ${chore.title}`}
                aria-pressed={armedDelete === chore.choreId}
                onClick={() => void removeChore(chore)}
              >
                {armedDelete === chore.choreId ? 'Sure?' : 'Delete'}
              </Pressable>
              {/* Later opens the lifecycle chooser: three snoozes and a
                  defer, all judged by the cap on the server (xvom08w4BgGT). */}
              <Pressable
                className="pear-btn ch-later"
                disabled={busy}
                aria-label={`Later — snooze or defer ${chore.title}`}
                aria-expanded={laterFor === chore.choreId}
                onClick={() => {
                  setLaterFor(laterFor === chore.choreId ? null : chore.choreId);
                  setLaterNote(null);
                }}
              >
                Later
              </Pressable>
            </div>

            {/* Reactions + the one-line receipt composer (MnorjNDwB3of). */}
            <Reactions chore={chore} me={me} reactBusyId={reactBusyId} onReact={react} />
            <NoteComposer chore={chore} noteFor={noteFor} noteDraft={noteDraft} onNoteDraft={setNoteDraft} busyId={busyId} onSaveNote={saveNote} onClose={() => { setNoteFor(null); setNoteDraft(''); }} />

            {laterFor === chore.choreId && (
              <LaterPanel chore={chore} busy={busy} pushLater={pushLater} />
            )}
          </div>
        );
      })}

      {done.length > 0 && (
        <>
          <p className="ch-done-heading">Done</p>
          {done.map(chore => (
            <div className="rq-row ch-row is-done" key={chore.choreId} data-chore-id={chore.choreId}>
              <div className="rq-head">
                <span className="rq-state is-accepted">Done</span>
                <span className="rq-when">
                  {chore.completedAt ? new Date(chore.completedAt).toLocaleDateString() : ''}
                </span>
              </div>
              <p className="pear-request-text rq-text">{chore.title}</p>
              <div className="ch-row-foot">
                <span className="ch-assignee is-static">{assigneeLabel(chore, me)}</span>
                {/* A closed row still takes a receipt (card MnorjNDwB3of):
                    either member can leave or replace the line, and react
                    to the finished work. */}
                <Pressable
                  className="pear-btn ch-note-open"
                  disabled={busyId === chore.choreId}
                  aria-label={`Add a done note to ${chore.title}`}
                  aria-expanded={noteFor === chore.choreId}
                  onClick={() => (noteFor === chore.choreId ? (setNoteFor(null), setNoteDraft('')) : openNote(chore))}
                >
                  Note
                </Pressable>
              </div>
              <Reactions chore={chore} me={me} reactBusyId={reactBusyId} onReact={react} />
              <NoteComposer chore={chore} noteFor={noteFor} noteDraft={noteDraft} onNoteDraft={setNoteDraft} busyId={busyId} onSaveNote={saveNote} onClose={() => { setNoteFor(null); setNoteDraft(''); }} />
            </div>
          ))}
        </>
      )}
    </div>
  );
}
